import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getCompany, updateCompany, getSubscription, uploadLogo, deleteLogo, getStaff, createStaff, updateStaff, deleteStaff, labelHelperStatus,
  labelHelperPair, pairPrintBridge, getPrintBridges, deletePrintBridge, PRINT_QUEUE_BASE } from '../api/client'
import { Save, Upload, Trash2, Plus, Pencil, Users, AlertTriangle, Download, CheckCircle2, Share, Menu as MenuIcon, Printer, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import { useSearchParams } from 'react-router-dom'
import { useInstallPrompt } from '../components/InstallAppButton'

const EU_ALLERGENS = [
  { key: 'gluten',       label: '1. Glutént tartalmazó gabonafélék (búza, rozs, árpa, zab stb.)' },
  { key: 'rakfelek',     label: '2. Rákfélék és a belőlük készült termékek' },
  { key: 'tojas',        label: '3. Tojás és a belőle készült termékek' },
  { key: 'halak',        label: '4. Hal és a belőle készült termékek' },
  { key: 'foldimogyoro', label: '5. Földimogyoró és a belőle készült termékek' },
  { key: 'szoja',        label: '6. Szójabab és a belőle készült termékek' },
  { key: 'tej',          label: '7. Tej és az abból készült termékek' },
  { key: 'diofele',      label: '8. Diófélék (mandula, mogyoró, dió, kesudió, pekándió stb.)' },
  { key: 'zeller',       label: '9. Zeller és a belőle készült termékek' },
  { key: 'mustar',       label: '10. Mustár és a belőle készült termékek' },
  { key: 'szezam',       label: '11. Szezámmag és a belőle készült termékek' },
  { key: 'ken_dioxid',   label: '12. Kén-dioxid és szulfitok (>10 mg/kg vagy 10 mg/l)' },
  { key: 'csillagfurt',  label: '13. Csillagfürt és a belőle készült termékek' },
  { key: 'puhatestuk',   label: '14. Puhatestűek és a belőlük készült termékek' },
]

const ROLES = ['Cukrász', 'Segédcukrász', 'Takarító', 'Raktáros', 'Vezető', 'Egyéb']

function StaffTab() {
  const qc = useQueryClient()
  const [editId, setEditId] = useState<number | null>(null)
  const [showForm, setShowForm] = useState(false)
  const emptyForm = { name: '', role: '', phone: '' }
  const [form, setForm] = useState({ ...emptyForm })

  const { data: staff = [] } = useQuery({
    queryKey: ['staff'],
    queryFn: () => getStaff().then(r => r.data),
  })

  const saveMut = useMutation({
    mutationFn: () => editId ? updateStaff(editId, form) : createStaff(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff'] })
      toast.success(editId ? 'Munkatárs frissítve' : 'Munkatárs hozzáadva')
      setForm({ ...emptyForm }); setEditId(null); setShowForm(false)
    },
    onError: () => toast.error('Hiba'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteStaff(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['staff'] }); toast.success('Törölve') },
  })

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }))

  function startEdit(m: any) {
    setEditId(m.id)
    setForm({ name: m.name, role: m.role || '', phone: m.phone || '' })
    setShowForm(true)
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-semibold text-gray-800">Munkatársak</h2>
          <p className="text-sm text-gray-500 mt-0.5">A HACCP naplókban operátorként kiválasztható személyek</p>
        </div>
        <button className="btn-primary flex items-center gap-2 text-sm"
          onClick={() => { setEditId(null); setForm({ ...emptyForm }); setShowForm(v => !v) }}>
          <Plus className="w-4 h-4" /> Új munkatárs
        </button>
      </div>

      {showForm && (
        <div className="rounded-xl border border-brand-200 bg-brand-50/30 p-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="label">Név *</label>
              <input className="input" value={form.name} onChange={set('name')} placeholder="pl. Kiss János" />
            </div>
            <div>
              <label className="label">Szerepkör</label>
              <select className="input" value={form.role} onChange={set('role')}>
                <option value="">— Válassz —</option>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Telefon (opcionális)</label>
              <input className="input" type="tel" value={form.phone} onChange={set('phone')} placeholder="+36..." />
            </div>
          </div>
          <div className="flex gap-2">
            <button className="btn-primary text-sm" onClick={() => saveMut.mutate()} disabled={!form.name || saveMut.isPending}>
              {editId ? 'Mentés' : 'Hozzáadás'}
            </button>
            <button className="btn-secondary text-sm" onClick={() => { setShowForm(false); setEditId(null); setForm({ ...emptyForm }) }}>Mégse</button>
          </div>
        </div>
      )}

      {(staff as any[]).length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Még nincs munkatárs hozzáadva</p>
          <p className="text-xs mt-1">Add hozzá a csapatodat, hogy a HACCP naplókban kiválaszthatók legyenek</p>
        </div>
      ) : (
        <div className="space-y-2">
          {(staff as any[]).map((m: any) => (
            <div key={m.id} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
              <div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-semibold text-sm">
                    {m.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium text-gray-800 text-sm">{m.name}</div>
                    <div className="flex items-center gap-2">
                      {m.role && <span className="text-xs text-gray-500">{m.role}</span>}
                      {m.phone && <span className="text-xs text-gray-400">{m.phone}</span>}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => startEdit(m)} className="text-gray-400 hover:text-blue-500 transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => deleteMut.mutate(m.id)} className="text-gray-400 hover:text-red-500 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function AppDownloadTab() {
  const { canInstall, promptInstall, installed } = useInstallPrompt()
  const ua = navigator.userAgent
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream
  const isAndroid = /Android/.test(ua)

  return (
    <div className="card p-6">
      <h2 className="font-semibold text-gray-800 mb-1">Alkalmazás letöltése</h2>
      <p className="text-sm text-gray-500 mb-5">
        A CukrászDoc telepíthető a telefonod kezdőképernyőjére vagy a géped asztalára, mint egy hagyományos alkalmazás — böngésző megnyitása nélkül is elérhető lesz.
      </p>

      {installed ? (
        <div className="flex items-center gap-2.5 p-4 rounded-xl bg-brand-50 border border-brand-200 text-brand-700">
          <CheckCircle2 className="w-5 h-5 shrink-0" />
          <span className="text-sm font-medium">Az alkalmazás már telepítve van ezen az eszközön.</span>
        </div>
      ) : canInstall ? (
        <button className="btn-primary flex items-center gap-2" onClick={promptInstall}>
          <Download className="w-4 h-4" />
          Alkalmazás telepítése
        </button>
      ) : isIOS ? (
        <div className="rounded-xl border border-gray-200 p-4 space-y-2.5">
          <p className="text-sm font-medium text-gray-800">Telepítés iPhone / iPad-en (Safari):</p>
          <div className="flex items-start gap-2.5 text-sm text-gray-600">
            <Share className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
            <span>Koppints a <strong>Megosztás</strong> ikonra a Safari alsó (vagy felső) sávjában</span>
          </div>
          <div className="flex items-start gap-2.5 text-sm text-gray-600">
            <Plus className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
            <span>Válaszd a <strong>„Kezdőképernyőhöz adás”</strong> lehetőséget, majd koppints a <strong>Hozzáadásra</strong></span>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 p-4 space-y-2.5">
          <p className="text-sm font-medium text-gray-800">
            Telepítés {isAndroid ? 'Androidon (Chrome)' : 'ezen a böngészőn'}:
          </p>
          <div className="flex items-start gap-2.5 text-sm text-gray-600">
            <MenuIcon className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
            <span>Nyisd meg a böngésző menüjét (⋮ vagy ⋯), és keresd az <strong>„Alkalmazás telepítése”</strong> vagy <strong>„Hozzáadás a kezdőképernyőhöz”</strong> pontot</span>
          </div>
          <p className="text-xs text-gray-400 pt-1">Ha nem látod ezt az opciót, próbáld Chrome vagy Edge böngészőben megnyitni az oldalt.</p>
        </div>
      )}
    </div>
  )
}

// Brother QL címkenyomtató segéd: állapot + letöltés + 3 lépéses útmutató
function LabelPrinterTab() {
  const qc = useQueryClient()
  const [status, setStatus] = useState<{ ok: boolean; printer: string | null; paired?: string[] } | null | undefined>(undefined)
  const check = () => { setStatus(undefined); labelHelperStatus().then(setStatus); qc.invalidateQueries({ queryKey: ['print-bridges'] }) }
  useEffect(check, [])
  const { data: bridges = [] } = useQuery({
    queryKey: ['print-bridges'],
    queryFn: () => getPrintBridges().then(r => r.data),
    refetchInterval: 15000,
  })
  const thisPaired = !!status?.paired?.includes(PRINT_QUEUE_BASE)
  const [pairing, setPairing] = useState(false)
  const pair = async () => {
    setPairing(true)
    try {
      const { data } = await pairPrintBridge()
      if (await labelHelperPair(data.token)) {
        toast.success('Párosítva — mostantól telefonról és tabletről is nyomtathatsz erre a gépre')
      } else {
        await deletePrintBridge(data.id)
        toast.error('A segéd nem válaszolt, próbáld újra')
      }
    } catch {
      toast.error('A párosítás nem sikerült')
    } finally {
      setPairing(false)
      check()
    }
  }
  const unpair = async (id: number) => {
    if (!confirm('Biztosan eltávolítod ezt a gépet? Utána telefonról nem lehet rá nyomtatni.')) return
    await deletePrintBridge(id)
    check()
  }

  return (
    <div className="card p-6 space-y-5">
      <div>
        <h2 className="font-semibold text-gray-800 mb-1 flex items-center gap-2"><Printer className="w-4 h-4" /> Címkenyomtató (Brother QL)</h2>
        <p className="text-sm text-gray-500">
          Brother QL-800, QL-810W vagy QL-820NWB nyomtatóval, 62 mm-es folyamatos tekerccsel (DK-22205) az etikettek és
          a konyhai címkék egy kattintással, nyomtatási ablak nélkül jönnek ki — és mindegyik pontosan olyan hosszú,
          amilyen a tartalma. Ha már van ilyen nyomtatód (pl. egy másik HACCP-programból), az is működik.
        </p>
      </div>

      <div className={`rounded-lg border p-3 flex items-center justify-between gap-3 ${
        status?.ok && status.printer ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
        <div className="text-sm">
          {status === undefined && <span className="text-gray-500">Ellenőrzés...</span>}
          {status === null && <span className="text-gray-600">A címkenyomtató segéd <b>nem fut</b> ezen a gépen.</span>}
          {status?.ok && status.printer && (
            <span className="text-green-800 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Kész a nyomtatásra: <b>{status.printer}</b></span>
          )}
          {status?.ok && !status.printer && (
            <span className="text-amber-700">A segéd fut, de nem talál Brother QL nyomtatót (meghajtó? bekapcsolva?).</span>
          )}
        </div>
        <button onClick={check} className="text-gray-400 hover:text-gray-600" title="Újraellenőrzés"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {/* Telefonról / tabletről: párosított gép(ek) */}
      <div className="rounded-lg border border-gray-200 p-3 space-y-2">
        <div className="text-sm font-medium text-gray-800">Nyomtatás telefonról és tabletről</div>
        <p className="text-xs text-gray-500">
          Párosítsd egyszer az üzlet gépét (amelyiken a segéd fut és a nyomtató csatlakozik). Utána bármelyik
          telefonról vagy tabletről a nyomtatás gomb erre a gépre küldi a címkét — amíg a gép be van kapcsolva.
        </p>
        {(bridges as any[]).map(b => (
          <div key={b.id} className="flex items-center justify-between gap-2 text-sm">
            <span className="flex items-center gap-2 min-w-0">
              <span className={`w-2 h-2 rounded-full shrink-0 ${b.online ? 'bg-green-500' : 'bg-gray-300'}`} />
              <span className="truncate">{b.name || 'Gép'}{b.printer_name ? ` · ${b.printer_name}` : ''}</span>
              <span className="text-xs text-gray-400 shrink-0">{b.online ? 'elérhető' : 'nem elérhető'}</span>
            </span>
            <button onClick={() => unpair(b.id)} className="text-gray-300 hover:text-red-500 shrink-0" title="Eltávolítás">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
        {status?.ok && !thisPaired && (
          <button onClick={pair} disabled={pairing} className="btn-primary text-sm inline-flex items-center gap-2">
            <Printer className="w-4 h-4" /> {pairing ? 'Párosítás...' : 'Ennek a gépnek a párosítása'}
          </button>
        )}
        {status?.ok && thisPaired && (
          <p className="text-xs text-green-700 flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Ez a gép párosítva van.</p>
        )}
        {status === null && (bridges as any[]).length === 0 && (
          <p className="text-xs text-gray-400">A párosításhoz nyisd meg ezt az oldalt azon a gépen, amelyiken a segéd fut.</p>
        )}
      </div>

      <ol className="space-y-3 text-sm text-gray-700 list-decimal pl-5">
        <li>
          <b>Nyomtató meghajtó:</b> telepítsd a Brother hivatalos meghajtóját (support.brother.com → a nyomtatód típusa →
          „Printer Driver”). A nyomtatón az <i>Editor Lite</i> lámpa ne világítson.
        </li>
        <li>
          <b>Címkenyomtató segéd:</b> töltsd le, és kattints rá duplán. Magától elindul a gép bekapcsolásakor is.
          <div className="mt-2">
            <a href="/letoltes/cimkenyomtato.exe" download className="btn-primary inline-flex items-center gap-2 text-sm">
              <Download className="w-4 h-4" /> Letöltés (Windows)
            </a>
          </div>
          <p className="text-xs text-gray-400 mt-1.5">
            Ha a Windows figyelmeztet („A Windows megvédte a számítógépet”): <i>További információ → Futtatás mindenképp</i>.
          </p>
        </li>
        <li>
          <b>Kész:</b> a Receptek → Etikett „Brother 62 mm” méreteinél és a HACCP naplók <Printer className="w-3.5 h-3.5 inline" /> gombjánál
          a címke azonnal kijön. A segéd nélkül is lehet nyomtatni, de akkor a böngésző nyomtatási ablaka jön fel.
        </li>
      </ol>
    </div>
  )
}

export default function SettingsPage() {
  const qc = useQueryClient()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<'cegadatok' | 'allergének' | 'munkatarsak' | 'nyomtato' | 'app'>(
    () => (searchParams.get('tab') === 'nyomtato' ? 'nyomtato' : 'cegadatok'))
  useEffect(() => {
    const payment = searchParams.get('payment')
    if (payment === 'cancelled') {
      toast.error('A fizetés megszakadt.')
    }
  }, [])

  const { data: company } = useQuery({
    queryKey: ['company'],
    queryFn: () => getCompany().then(r => r.data),
  })

  const fileInputRef = useRef<HTMLInputElement>(null)

  const uploadLogoMut = useMutation({
    mutationFn: (file: File) => uploadLogo(file),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['company'] }); toast.success('Logó feltöltve') },
    onError: () => toast.error('Hiba a feltöltéskor'),
  })

  const deleteLogoMut = useMutation({
    mutationFn: () => deleteLogo(),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['company'] }); toast.success('Logó törölve') },
    onError: () => toast.error('Hiba a törléskor'),
  })

  const [form, setForm] = useState({
    name: '', bakery_name: '', tax_number: '', address: '', site_address: '',
    nebih_number: '', contact_name: '', phone: '', email: '',
  })
  const [facilityAllergens, setFacilityAllergens] = useState<string[]>([])

  useEffect(() => {
    if (company) {
      setForm({
        name: company.name || '',
        bakery_name: company.bakery_name || '',
        tax_number: company.tax_number || '',
        address: company.address || '',
        site_address: company.site_address || '',
        nebih_number: company.nebih_number || '',
        contact_name: company.contact_name || '',
        phone: company.phone || '',
        email: company.email || '',
      })
      setFacilityAllergens(company.facility_allergens || [])
    }
  }, [company])

  const saveMut = useMutation({
    mutationFn: () => updateCompany({ ...form, facility_allergens: facilityAllergens }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['company'] }); toast.success('Beállítások mentve') },
    onError: () => toast.error('Hiba a mentéskor'),
  })

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }))

  const toggleAllergen = (key: string) =>
    setFacilityAllergens(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    )

  const tabs = [
    { key: 'cegadatok', label: 'Cégadatok' },
    { key: 'allergének', label: 'Üzemi allergének' },
    { key: 'munkatarsak', label: 'Munkatársak' },
    { key: 'nyomtato', label: 'Címkenyomtató' },
    { key: 'app', label: 'App letöltése' },
  ] as const

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Beállítások</h1>
      <p className="text-sm text-gray-500 mb-5">
        Ezek az adatok megjelennek a generált PDF dokumentumokon és etikett-címkéken.
      </p>

      {/* Tabok */}
      <div className="flex gap-0.5 border-b border-gray-200 mb-6 overflow-x-auto">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-3 sm:px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.key
                ? 'border-brand-500 text-brand-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Cégadatok tab ── */}
      {activeTab === 'cegadatok' && (
        <div className="card p-6 space-y-4">
          {!form.site_address && (
            <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <span>A <strong>telephely cím</strong> nincs megadva. A generált dokumentumokon és etikett-cimkéken a gyártói adatok hiányosak lesznek, ami hatósági ellenőrzés során megfelelőségi problémát okozhat.</span>
            </div>
          )}
          {[
            ['Cukrászda neve', 'bakery_name', 'text'],
            ['Cégnév (jogi név)', 'name', 'text'],
            ['Adószám', 'tax_number', 'text'],
            ['Székhely címe', 'address', 'text'],
            ['Telephely címe *', 'site_address', 'text'],
            ['NÉBIH engedélyszám', 'nebih_number', 'text'],
            ['Kapcsolattartó neve', 'contact_name', 'text'],
            ['Telefonszám', 'phone', 'tel'],
            ['E-mail', 'email', 'email'],
          ].map(([label, key, type]) => (
            <div key={key}>
              <label className="label">{label}</label>
              <input
                className={`input${key === 'site_address' && !form.site_address ? ' border-amber-300' : ''}`}
                type={type}
                value={(form as any)[key]}
                onChange={set(key)}
              />
            </div>
          ))}
          {/* Logo feltöltés */}
          <div>
            <label className="label">Logó (megjelenik a gyártmánylapon)</label>
            {company?.logo_data ? (
              <div className="flex items-center gap-4 mt-1">
                <img src={company.logo_data} alt="Logó" className="h-14 object-contain border border-gray-200 rounded p-1 bg-white" />
                <button
                  className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-800 transition-colors"
                  onClick={() => deleteLogoMut.mutate()}
                  disabled={deleteLogoMut.isPending}
                >
                  <Trash2 className="w-4 h-4" />
                  {deleteLogoMut.isPending ? 'Törlés...' : 'Logó törlése'}
                </button>
              </div>
            ) : (
              <div
                className="mt-1 flex items-center gap-3 border-2 border-dashed border-gray-300 rounded-lg px-4 py-3 cursor-pointer hover:border-brand-400 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-500">
                  {uploadLogoMut.isPending ? 'Feltöltés...' : 'Kattints a logó feltöltéséhez (PNG, JPG, max 2 MB)'}
                </span>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={e => {
                const f = e.target.files?.[0]
                if (f) uploadLogoMut.mutate(f)
                e.target.value = ''
              }}
            />
          </div>

          <button className="btn-primary flex items-center gap-2 mt-2"
            onClick={() => {
              if (!form.site_address.trim()) {
                toast.error('A telephely cím kitöltése kötelező!')
                return
              }
              saveMut.mutate()
            }}
            disabled={saveMut.isPending}>
            <Save className="w-4 h-4" />
            {saveMut.isPending ? 'Mentés...' : 'Mentés'}
          </button>
        </div>
      )}

      {/* ── Üzemi allergének tab ── */}
      {activeTab === 'allergének' && (
        <div className="card p-6">
          <h2 className="font-semibold text-gray-800 mb-1">Üzemben előforduló allergének</h2>
          <p className="text-sm text-gray-500 mb-5">
            Jelöld be, mely allergének fordulnak elő az üzemedben (alapanyagként vagy feldolgozás során).
            Ezek minden gyártmánylapon megjelennek a <strong>Keresztszennyeződés</strong> oszlopban — akkor is,
            ha az adott recept maga nem tartalmazza.
          </p>
          <div className="space-y-2">
            {EU_ALLERGENS.map(a => (
              <label key={a.key}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  facilityAllergens.includes(a.key)
                    ? 'border-brand-400 bg-brand-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}>
                <input
                  type="checkbox"
                  className="mt-0.5 accent-brand-500 w-4 h-4 shrink-0"
                  checked={facilityAllergens.includes(a.key)}
                  onChange={() => toggleAllergen(a.key)}
                />
                <span className="text-sm text-gray-800">{a.label}</span>
              </label>
            ))}
          </div>
          <div className="mt-5 flex items-center justify-between">
            <p className="text-xs text-gray-400">
              {facilityAllergens.length} allergén van bejelölve
            </p>
            <button className="btn-primary flex items-center gap-2"
              onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
              <Save className="w-4 h-4" />
              {saveMut.isPending ? 'Mentés...' : 'Mentés'}
            </button>
          </div>
        </div>
      )}

      {/* ── Munkatársak tab ── */}
      {activeTab === 'munkatarsak' && (
        <StaffTab />
      )}

      {/* ── Címkenyomtató tab ── */}
      {activeTab === 'nyomtato' && (
        <LabelPrinterTab />
      )}

      {/* ── App letöltése tab ── */}
      {activeTab === 'app' && (
        <AppDownloadTab />
      )}

    </div>
  )
}
